import type { Auth0ContextInterface } from "@auth0/auth0-react";
import { DEFAULT_LOCALE, DEFAULT_PARTNER, getLanguageCode, isKnownLocaleTag } from "@pandas/core-ui";
import { canonicalizeRootLocaleUrl, type PartnerRouteContext } from "@pandas/core-ui/route";
import { createRootRouteWithContext, Outlet, redirect } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect } from "react";
import TagManager from "react-gtm-module";
import AppLayout from "../components/Layout/AppLayout";
import { servicesOrchestratorValues } from "../config/services";
import { platformApi } from "../services";
import { fetchPartnerDefaultLocale } from "../utils/localeResolver";

export type RouterContext = PartnerRouteContext & {
	auth: Auth0ContextInterface;
};

// const isProduction = servicesOrchestratorValues.app.env === "production";
// const isUat = servicesOrchestratorValues.app.env === "uat";
// const showDevtools = !(isProduction || isUat);
const showDevtools = false;

const resolveRetailLocale = (legacy: string) => (isKnownLocaleTag(legacy) ? getLanguageCode(legacy) : DEFAULT_LOCALE);

export const Route = createRootRouteWithContext<RouterContext>()({
	beforeLoad: async ({ location, search, context }) => {
		if (location.pathname !== "/") {
			const result = await canonicalizeRootLocaleUrl({
				pathname: location.pathname,
				search,
				isLocale: isKnownLocaleTag,
				resolveLocale: resolveRetailLocale,
				reservedPartnerSegments: context.reservedPartnerRouteSegments,
				resolvePartner: async (s) => {
					if (typeof s.partnerCode === "string") return s.partnerCode;
					if (context.auth.isAuthenticated) {
						const locationId = context.auth.user?.platform_app_metadata?.location_id;
						if (locationId) {
							try {
								const locationData = await platformApi.getLocationAndPartner(locationId);
								return locationData.Partner.code;
							} catch {
								// fall through to default
							}
						}
					}
					return DEFAULT_PARTNER;
				},
			});

			if (result?.kind === "redirect") throw redirect({ href: result.href, replace: true });
			// `unresolvable` cannot occur — resolvePartner always returns DEFAULT_PARTNER as a last resort.
		}

		if (location.pathname !== "/" || !context.auth.isAuthenticated) return;

		const user = context.auth.user;
		const isAdmin = user?.platform_roles?.includes("RetailApp Admin") ?? false;
		const locationId = user?.platform_app_metadata?.location_id;

		let locale = DEFAULT_LOCALE;
		let partner = DEFAULT_PARTNER;

		if (!isAdmin && locationId) {
			try {
				const locationData = await platformApi.getLocationAndPartner(locationId);
				partner = locationData.Partner.code;
				locale = await fetchPartnerDefaultLocale(partner);
			} catch {
				// keep defaults
			}
		}

		throw redirect({
			to: "/$locale/$partner/home",
			params: { locale, partner },
			replace: true,
		});
	},
	component: RootComponent,
});

function RootComponent() {
	useEffect(() => {
		TagManager.initialize({
			gtmId: servicesOrchestratorValues.app.googleTagManagerId,
		});
	}, []);

	return (
		<>
			<AppLayout>
				<Outlet />
			</AppLayout>
			{showDevtools && <TanStackRouterDevtools />}
		</>
	);
}
